import {
    IconActivity,
    IconBell,
    IconHome,
    IconLoader,
    IconModule1On1,
    IconModule360,
    IconModuleObjectives,
    IconModulePraise,
    IconModuleReviews,
    IconSettings,
    IconSparkles,
    IconTeam,
} from 'featherico';
import type { Observable } from 'ladda-observable';
import { negate, stubTrue } from 'lodash/fp';
import type * as React from 'react';
import type { Feature, UserCompanyDto } from '../../../../../types/CompanyDtos';
import type { CurrentUserDto } from '../../../../../types/UserDtos';
import api from '../../../../api';
import * as R from '../../../../routes';
import { mapObservable } from '../../../../services/observables';
import type { PillBoxColor } from '../../../components/PillBox';
import { NAVIGATION } from '../../../intercom/intercom-data-targets';
import { isFeatureEnabledForUser } from '../../../services/Features';
import r from '../../../services/Replacement';
import { hasBasicHr, isAnyAdmin, isGuest, isHr } from '../../../services/User';
import type { EapState } from '../../Admin/EarlyAccessProgram/useEapState';
import { countAdminErrors } from '../../Admin/Overview/service';
import { shouldShowAiCoach } from '../../AiCoach/aiFeatureLimits';
import { showGeneralFeedbackWording } from '../../YourFeedback/services/feedback';

const featureStatus =
    (...features: Feature[]) =>
    (enabledFeatures: UserCompanyDto['enabledFeatures']) =>
        features.some(feature => enabledFeatures[feature]);

export interface NavLink {
    title: string;
    route: string;
    alsoMatch?: string[];
    icon: React.ReactNode;
    /** For admins false will give faded-out appearance, for others will filter out the link */
    isFeatureEnabled?: (enabledFeatures: UserCompanyDto['enabledFeatures']) => boolean;
    /** Determine whether the link is visible in the navigation */
    isAvailableTo: (currentUser: CurrentUserDto) => boolean;
    getNotification?: () => Observable<NavLinkNotification>;
    titleClass?: string;
    trackingLabel: string;
    testId: string; // These IDs are separate from tracking and intercom IDs to avoid tests breaking when they change.
    dataIntercomTarget?: string;
    onClick?: (e: React.MouseEvent) => void;
    openInNewTab?: boolean;
}

export interface NavLinkNotification {
    count: number;
    color: PillBoxColor;
}
export const getLinks = (eapState: EapState): NavLink[] => [
    {
        title: 'Home',
        route: R.BASE + R.DASHBOARD,
        icon: <IconHome />,
        isAvailableTo: negate(isGuest),
        trackingLabel: 'home',
        testId: 'nav-home',
    },
    {
        title: 'Inbox',
        route: R.BASE + R.INBOX,
        icon: <IconBell />,
        isAvailableTo: user => !isGuest(user) && isFeatureEnabledForUser('FEATURE_inboxPage', user),
        getNotification: () =>
            mapObservable(({ count }) => ({ count, color: 'RED' }), api.notificationsCount.getCount.createObservable()),
        trackingLabel: 'inbox',
        testId: 'nav-inbox',
    },
    {
        title: r('praise.menu.title'),
        route: R.BASE + R.PRAISE_STREAM,
        icon: <IconModulePraise />,
        isFeatureEnabled: featureStatus('praise'),
        isAvailableTo: negate(isGuest),
        trackingLabel: 'praise',
        testId: 'nav-praise',
        dataIntercomTarget: NAVIGATION.EMPLOYEE_PRAISE,
    },
    {
        title: r('meeting.name.plural'),
        route: R.BASE + R.MEETING_OVERVIEW,
        alsoMatch: [R.BASE + R.MEETING_DEDICATED],
        icon: <IconModule1On1 />,
        isFeatureEnabled: featureStatus('meetings'),
        isAvailableTo: negate(isGuest),
        trackingLabel: 'meetings',
        testId: 'nav-meetings',
        dataIntercomTarget: NAVIGATION.EMPLOYEE_MEETINGS,
    },
    {
        title: r('surveys.plural.big'),
        route: R.BASE + R.YOUR_SURVEYS,
        icon: <IconActivity />,
        isFeatureEnabled: f => f.surveys,
        isAvailableTo: user => (user.isGuest ? false : isFeatureEnabledForUser('surveys', user) || isHr(user)),
        trackingLabel: 'surveys',
        testId: 'nav-surveys',
        dataIntercomTarget: NAVIGATION.EMPLOYEE_SURVEYS,
    },
    {
        title: r('objective.plural.title'),
        route: R.BASE + R.OBJECTIVES,
        icon: <IconModuleObjectives />,
        isFeatureEnabled: featureStatus('objective'),
        isAvailableTo: negate(isGuest),
        trackingLabel: 'objectives',
        testId: 'nav-objectives',
        dataIntercomTarget: NAVIGATION.EMPLOYEE_OBJECTIVES,
    },
    {
        title: showGeneralFeedbackWording ? r('fr.menu.unified.title') : r('fr.menu.title'),
        route: R.BASE + R.FEEDBACK_USER,
        alsoMatch: [R.BASE + R.FEEDBACK_USER_TODO],
        icon: <IconModule360 />,
        isFeatureEnabled: featureStatus('feedback', 'requestFeedback'),
        isAvailableTo: stubTrue,
        trackingLabel: '360s',
        testId: 'nav-360s',
        dataIntercomTarget: NAVIGATION.EMPLOYEE_FEEDBACK,
    },
    {
        title: r('pr.menu.title'),
        route: R.BASE + R.REVIEWS,
        alsoMatch: ['/appraisalView', '/appraisalDetails'],
        icon: <IconModuleReviews />,
        isFeatureEnabled: featureStatus('review'),
        isAvailableTo: negate(isGuest),
        trackingLabel: 'reviews',
        testId: 'nav-reviews',
        dataIntercomTarget: NAVIGATION.EMPLOYEE_REVIEWS,
    },
    {
        title: 'People',
        route: R.BASE + R.PEOPLE,
        icon: <IconTeam />,
        isAvailableTo: u => !isGuest(u),
        trackingLabel: 'people',
        testId: 'nav-people',
    },
    {
        title: 'AI Coach',
        route: R.BASE + R.AI_COACH,
        icon: <IconSparkles />,
        isAvailableTo: () => shouldShowAiCoach('workspace'),
        trackingLabel: 'employeeAiCoach',
        testId: 'nav-employeeAiCoach',
    },
    {
        title: 'Settings',
        route: R.BASE + R.SETTINGS,
        alsoMatch: [R.BASE + R.INSIGHTS],
        icon: <IconSettings />,
        isAvailableTo: u => isAnyAdmin(u),
        getNotification: () =>
            mapObservable(
                l => ({ count: countAdminErrors(l), color: 'RED' }),
                api.admin.getSettingsLinks.createObservable()
            ),
        trackingLabel: 'adminSettings',
        testId: 'nav-adminSettings',
    },
    {
        title: 'Coming Soon',
        route: R.BASE + R.COMING_SOON,
        icon: <IconLoader />,
        isAvailableTo: u =>
            eapState !== 'HAS_NONE' && hasBasicHr(u) && isFeatureEnabledForUser('FEATURE_earlyAccessProgram', u),
        titleClass: eapState === 'HAS_NEW' ? 'coming-soon' : '',
        trackingLabel: 'adminEarlyAccessProgram',
        testId: 'nav-adminEarlyAccessProgram',
    },
];
